"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { PillToggle } from "@/components/ui/controls/PillToggle";
import { usePostData } from "../../context/PostDataContext";
import { PostGridDefault } from "../shared/PostGridDefault";
import { PostGridStandard } from "../shared/PostGridStandard";
import { PostGridMinimal } from "../shared/PostGridMinimal";
import type { PostsVariant } from "../shared/types";
import type { Post } from "@/payload-types";
import type { PostLike } from "@/lib/navigation/createUrlBuilder";
import { getMediaFieldUrl } from "@/utilities/media/getMediaUrl";
export interface TabbedPostsProps {
  /** Visual variant of the post grids */
  variant?: PostsVariant;
  /** Custom container className */
  className?: string;
  /** Default active tab */
  defaultTab?: "related" | "category";
  /** Force a fixed number of columns (1, 2, or 3). If not set, uses responsive defaults. */
  columns?: 1 | 2 | 3;
  /** Build the link URL for a post. Receives the full post so the route can respect its type. */
  buildLink: (post: PostLike) => string;
  /** Localized label for "Related" tab */
  relatedLabel: string;
  /** Localized label for "Latest from category" tab */
  categoryLabel: string;
  /** Localized label for "Latest news" tab */
  latestLabel: string;
}

/**
 * TabbedPosts - Displays a tabbed interface switching between
 * related posts and latest from category.
 * Returns null if both related posts and category posts are empty.
 */
export function TabbedPosts(t0) {
  const $ = _c(35);
  const {
    variant: t1,
    className: t2,
    defaultTab: t3,
    columns,
    buildLink,
    relatedLabel,
    categoryLabel,
    latestLabel
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const className = t2 === undefined ? "pt-12" : t2;
  const defaultTab = t3 === undefined ? "related" : t3;
  const {
    relatedPosts,
    categoryPosts,
    latestPosts,
    locale
  } = usePostData();
  const hasRelated = relatedPosts && relatedPosts.length > 0;
  const hasCategory = categoryPosts && categoryPosts.length > 0;
  const hasLatest = latestPosts && latestPosts.length > 0;
  const initialTab = defaultTab === "related" && hasRelated ? "related" : hasCategory ? "category" : hasLatest ? "latest" : "related";
  const [activeTab, setActiveTab] = useState(initialTab);
  if (!hasRelated && !hasCategory && !hasLatest) {
    return null;
  }
  const displayPosts = activeTab === "related" ? relatedPosts : activeTab === "category" ? categoryPosts : latestPosts;
  let t4;
  if ($[0] !== locale) {
    t4 = dateString => {
      if (!dateString) {
        return "";
      }
      const date = new Date(dateString);
      return date.toLocaleDateString(locale, {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
    };
    $[0] = locale;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const formatDate = t4;
  const getImageUrl = _temp;
  let t5;
  if ($[2] !== displayPosts) {
    t5 = displayPosts || [];
    $[2] = displayPosts;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== buildLink || $[5] !== columns || $[6] !== formatDate || $[7] !== t5) {
    t6 = {
      posts: t5,
      formatDate,
      getImageUrl,
      className: "",
      columns,
      buildLink
    };
    $[4] = buildLink;
    $[5] = columns;
    $[6] = formatDate;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const gridProps = t6;
  let t7;
  if ($[9] !== gridProps || $[10] !== variant) {
    t7 = () => {
      switch (variant) {
        case "default":
          {
            return <PostGridDefault {...gridProps} />;
          }
        case "standard":
          {
            return <PostGridStandard {...gridProps} />;
          }
        case "minimal":
          {
            return <PostGridMinimal {...gridProps} />;
          }
        default:
          {
            return null;
          }
      }
    };
    $[9] = gridProps;
    $[10] = variant;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const renderGrid = t7;
  let tabOptions;
  if ($[12] !== categoryLabel || $[13] !== hasCategory || $[14] !== hasLatest || $[15] !== hasRelated || $[16] !== latestLabel || $[17] !== relatedLabel) {
    tabOptions = [];
    if (hasRelated) {
      let t8;
      if ($[19] !== relatedLabel) {
        t8 = {
          id: "related",
          label: relatedLabel
        };
        $[19] = relatedLabel;
        $[20] = t8;
      } else {
        t8 = $[20];
      }
      tabOptions.push(t8);
    }
    if (hasCategory) {
      let t8;
      if ($[21] !== categoryLabel) {
        t8 = {
          id: "category",
          label: categoryLabel
        };
        $[21] = categoryLabel;
        $[22] = t8;
      } else {
        t8 = $[22];
      }
      tabOptions.push(t8);
    }
    if (hasLatest && !hasCategory) {
      let t8;
      if ($[23] !== latestLabel) {
        t8 = {
          id: "latest",
          label: latestLabel
        };
        $[23] = latestLabel;
        $[24] = t8;
      } else {
        t8 = $[24];
      }
      tabOptions.push(t8);
    }
    $[12] = categoryLabel;
    $[13] = hasCategory;
    $[14] = hasLatest;
    $[15] = hasRelated;
    $[16] = latestLabel;
    $[17] = relatedLabel;
    $[18] = tabOptions;
  } else {
    tabOptions = $[18];
  }
  const showTitleOnly = tabOptions.length === 1;
  let t8;
  if ($[25] !== activeTab || $[26] !== showTitleOnly || $[27] !== tabOptions) {
    t8 = <div className="mb-6">{showTitleOnly ? <h2 className="text-xl font-bold">{tabOptions[0].label}</h2> : <PillToggle options={tabOptions} activeId={activeTab} onChange={setActiveTab} size="md" />}</div>;
    $[25] = activeTab;
    $[26] = showTitleOnly;
    $[27] = tabOptions;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== renderGrid) {
    t9 = renderGrid();
    $[29] = renderGrid;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== className || $[32] !== t8 || $[33] !== t9) {
    t10 = <section className={className}>{t8}{t9}</section>;
    $[31] = className;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
}
function _temp(heroImage) {
  return getMediaFieldUrl(heroImage, "medium") ?? undefined;
}